import { Link, useNavigate } from 'react-router-dom';
import { Mold, ProductPart } from '../../types/mold';
import { Column } from './types';
import { Eye } from 'lucide-react';
import MoldStatus from '../shared/MoldStatus';
import MoldShotProgressGradient from '../shared/MoldShotProgressGradient';
import CustomerCodeLabel from '../shared/CustomerCodeLabel';
import { useState } from 'react';

interface TableCellProps {
  mold: Mold;
  column: Column;
  selectedMolds: string[];
}

export const SelectCell: React.FC<{
  isSelected: boolean;
  disabled?: boolean;
  onClick?: () => void;
}> = ({ isSelected, disabled, onClick }) => (
  <input
    type="checkbox"
    disabled={disabled || false}
    checked={isSelected}
    className="form-checkbox h-5 w-5 text-blue-600 rounded"
    onChange={onClick}
  />
);

export const ProductPartsCell: React.FC<{
  parts: ProductPart[],
  bgColor?: string,
  textColor?: string,
  useGridLayout?: boolean,
  showPreview?: boolean,
}> = ({
  parts, bgColor = 'bg-gray-100', textColor = 'text-gray-800', useGridLayout = false,
  showPreview = false,
}) => {
  const [showAllParts, setShowAllParts] = useState(!showPreview);

  const ClickToSeeMore = () => {
    if (!showAllParts && parts.length > 2) {
      return (
        <span
          key={'showPreview'}
          className={`w-auto max-w-8 px-1 py-1 bg-gray-400 text-white rounded-full text-xs shadow-lg ${textColor}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setShowAllParts(true)
          }}
          style={{ cursor: 'pointer' }}
        >
          + {parts.length - 2}
        </span>
      );
    }
    return <></>
  }

  const filteredParts = showAllParts ? parts : parts.filter((_p, index) => index < 1);
  if (parts.length === 0) {
    return <span className="text-gray-500">-</span>;
  }
  const gridLayout = useGridLayout ? 'grid grid-cols-1 sm:grid-cols-3 gap-2 items-center' : 'flex flex-col gap-2';
  return (
    <div className={gridLayout}>
      {filteredParts.map((part, index) => (
        <span
          key={`${part.partNumber}-${index}`}
          className={`px-2 py-1 ${bgColor} rounded-full text-xs xl:text-sm font-medium ${textColor}`}
        >
          <div className="flex flex-row items-center justify-between">
            <p>{part.partNumber}</p>
            <ClickToSeeMore />
          </div>
          
        </span>
      ))}
    </div>
  );
  }

export const MoldListCell: React.FC<{
  molds: Mold[],
  bgColor?: string,
  textColor?: string,
  useGridLayout?: boolean,
}> = ({ molds, bgColor = 'bg-blue-100', textColor = 'text-gray-800', useGridLayout = false }) => {
  if (molds.length === 0) {
    return <span className="text-gray-500">-</span>;
  }
  const gridLayout = useGridLayout ? 'grid grid-cols-1 sm:grid-cols-3 gap-2 items-center text-center' : 'flex flex-col gap-2';
  return (
    <div className={gridLayout}>
      {molds.map((mold, index) => (
        <span
          key={`${mold.moldNumber}-${index}`}
          className={`px-2 py-1 ${bgColor} rounded-full text-sm font-medium ${textColor}`}
        ><Link
          key={mold?.id}
          to={`/molds/${mold?.id}`}
          className="text-blue-600 hover:underline"
        >
            {mold?.sbiId ?? '-'}
          </Link>
        </span>
      ))}
    </div>
  );
}

const ActionCell: React.FC<{ moldId: string }> = ({ moldId }) => {
  const navigate = useNavigate();

  return (
    <button
      onClick={() => navigate(`/molds/${moldId}`)}
      className="flex items-center justify-center px-4 py-2 text-sm text-gray-700 w-full hover:bg-gray-100 transition-colors duration-200"
    >
      <Eye
        size={16}
        className="text-gray-500 group-hover:text-blue-500 transition-colors duration-200"
      />
    </button>
  );
};

const DefaultCell: React.FC<{ value: any }> = ({ value }) => {
  if (value === null || value === undefined) {
    return <>-</>;
  }

  if (typeof value === 'object') {
    if ('code' in value) {
      return <>{value.code}</>;
    }
    if (Array.isArray(value)) {
      return <>{value.join(', ')}</>;
    }
    return <>{JSON.stringify(value)}</>;
  }

  return <>{String(value)}</>;
};

export const TableCell: React.FC<TableCellProps> = ({
  mold,
  column,
  selectedMolds,
}) => {
  switch (column.key) {
    case 'select':
      return <SelectCell isSelected={selectedMolds.includes(mold.id)} />;

    case 'moldNumber':
      return <>{mold.moldNumber}</>;

    case 'name':
      return <>{mold.name}</>;

    case 'customer':
      return <CustomerCodeLabel customer={mold.customer} />

    case 'status':
      return <MoldStatus mold={mold} />;

    case 'productParts':
      return <ProductPartsCell parts={mold.productParts} />;

    case 'actions':
      return <ActionCell moldId={mold.id} />;

    case 'shotCount':
      return (
        <MoldShotProgressGradient
          shotCount={mold.cycleShotCount}
          servicingAlertShotThreshold={mold.servicingAlertShotThreshold}
          servicingMaxShotThreshold={mold.servicingMaxShotThreshold}
          maxShotCount={mold.maxShotCount}
        />
      );

    default:
      return <DefaultCell value={mold[column.key as keyof Mold]} />;
  }
};

export default TableCell;
